import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Copy, Package, MapPin, Crown, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { API, toast } from '@/App';
import DeliveryEstimateSummary from '@/components/shared/DeliveryEstimateSummary';
import { logClientError } from '@/utils/clientError';
import { formatDeliveryStatusLabel } from '@/utils/displayLabels';

const DeliveryList = ({ onRefresh }) => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState('');

  useEffect(() => {
    loadDeliveries();
  }, []);

  const loadDeliveries = async () => {
    try {
      const response = await axios.get(`${API}/deliveries`);
      setDeliveries(response.data);
    } catch (error) {
      logClientError('deliveries.list', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending_payment: { label: 'En attente de paiement', class: 'bg-amber-100 text-amber-700' },
      paid: { label: 'Payé', class: 'bg-blue-100 text-blue-700' },
      pickup_in_progress: { label: 'Ramassage', class: 'bg-indigo-100 text-indigo-700' },
      picked_up: { label: 'Récupéré', class: 'bg-purple-100 text-purple-700' },
      in_transit: { label: 'En transit', class: 'bg-cyan-100 text-cyan-700' },
      at_relay: { label: 'Au point relais', class: 'bg-orange-100 text-orange-700' },
      out_for_delivery: { label: 'En livraison', class: 'bg-green-100 text-green-700' },
      delivered: { label: 'Livré', class: 'bg-green-100 text-green-700' },
      cancelled: { label: 'Annulé', class: 'bg-red-100 text-red-700' }
    };
    const info = statusMap[status] || { label: formatDeliveryStatusLabel(status), class: 'bg-gray-100 text-gray-700' };
    return <Badge className={info.class}>{info.label}</Badge>;
  };

  const getServiceTier = (delivery) => {
    if (delivery?.service_tier) return delivery.service_tier;
    return delivery?.is_express ? 'express' : 'standard';
  };

  const handleDuplicateDelivery = async (event, deliveryId) => {
    event.stopPropagation();
    if (duplicatingId === deliveryId) {
      return;
    }

    setDuplicatingId(deliveryId);
    try {
      const response = await axios.post(`${API}/deliveries/${deliveryId}/duplicate`);
      const payload = response.data?.data || response.data;
      toast.success(response.data?.message || 'Commande dupliquee. Verifiez le nouveau client avant validation.');
      navigate('/vendeur/create', {
        state: {
          deliveryDuplicate: payload,
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de dupliquer cette commande');
    } finally {
      setDuplicatingId('');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </CardContent>
      </Card>
    );
  }

  if (deliveries.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucune livraison pour le moment</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle>Mes livraisons</CardTitle>
          <CardDescription>Cliquez sur une livraison pour voir les détails</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {deliveries.map((delivery) => {
              const serviceTier = getServiceTier(delivery);
              const isExpressTier = serviceTier === 'express';
              const isInsured = Boolean(delivery.is_insured);

              return (
                <Card
                  key={delivery.id}
                  data-testid={`delivery-card-${delivery.id}`}
                  className={`card-hover cursor-pointer ${isExpressTier ? 'border-amber-200 bg-amber-50/20' : ''}`}
                  onClick={() => navigate(`/vendeur/delivery/${delivery.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-lg">#{delivery.id.slice(0, 8)}</p>
                          {isExpressTier && (
                            <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white flex items-center gap-1">
                              <Crown className="w-3 h-3" />
                              EXPRESS
                            </Badge>
                          )}
                          {isInsured && (
                            <Badge className="bg-cyan-600 text-white flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" />
                              Assurance
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">Client : {delivery.client_info.full_name}</p>
                      </div>
                      {getStatusBadge(delivery.status)}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-red-600 mt-1" />
                        <div className="text-sm">
                          <p className="font-medium">Ramassage</p>
                          <p className="text-gray-600">{delivery.pickup_city} - {delivery.pickup_district}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-green-600 mt-1" />
                        <div className="text-sm">
                          <p className="font-medium">Livraison</p>
                          <p className="text-gray-600">{delivery.delivery_city} - {delivery.delivery_district}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 gap-3 border-t pt-3">
                      <div className="flex items-center gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Poids : </span>
                          <span className="font-medium">{delivery.weight} kg</span>
                        </div>
                        {delivery.livreur_name && (
                          <div>
                            <span className="text-gray-500">Livreur : </span>
                            <span className="font-medium">{delivery.livreur_name}</span>
                          </div>
                        )}
                        <DeliveryEstimateSummary
                          source={delivery}
                          variant="compact"
                          testId={`delivery-list-estimate-${delivery.id}`}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          data-testid={`duplicate-delivery-btn-${delivery.id}`}
                          variant="outline"
                          size="sm"
                          onClick={(event) => handleDuplicateDelivery(event, delivery.id)}
                          disabled={duplicatingId === delivery.id}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          {duplicatingId === delivery.id ? 'Preparation...' : 'Dupliquer'}
                        </Button>
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600">{delivery.cost} {delivery.currency}</p>
                          {(delivery.express_surcharge_amount || delivery.insurance_amount) ? (
                            <p className="text-xs text-gray-500">
                              Base {Number(delivery.transport_amount ?? delivery.cost ?? 0).toLocaleString()} {delivery.currency}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeliveryList;
